import React from 'react'
import PropTypes from 'prop-types'
import Event from '@app/models/event'
import classnames from 'classnames'

import SVG from 'react-inlinesvg'
import calendar from '@icons/calendar-header.svg'
import clocks from '@icons/tours/tour-limit.svg'
import marker from '@icons/list-marker.svg'
import { getTourStatus, TourName, TourStatus } from '@app/helpers/tourStatusHelper'

export const heading = (tour) => {
  return I18n.t('landing.' + tour + '_round')
}

export const preparatoryRoundDates = (event, opts = {}) => {
  const start = I18n.l('date.formats.state_label_start', event.preparatory_tour_starts_at, opts)
  const end = I18n.l('date.formats.state_label_end', event.preparatory_tour_ends_at, opts)
  return `${start} – ${end}`
}

export const trialRoundDates = (event, opts = {}) => {
  const start = I18n.l('date.formats.state_label_start', event.trial_tour_starts_at, opts)
  const end = I18n.l('date.formats.state_label_end', event.trial_tour_ends_at, opts)
  return `${start} – ${end}`
}

export const mainRoundDates = (event, opts = {}) => {
  const start = I18n.l('date.formats.state_label_start', event.main_tour_starts_at, opts)
  const end = I18n.l('date.formats.state_label_end', event.main_tour_ends_at, opts)
  return `${start} – ${end}`
}

const getOrdinalSuffix = (day) => {
  const teen = day % 100
  if (teen >= 11 && teen <= 13) { return 'th' }
  switch (day % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

export const mainRoundStartDate = (event, opts = {}) => {
  const formatted = I18n.l('date.formats.month_day_no_year', event.main_tour_starts_at, opts)
  const match = formatted && formatted.match(/^([A-Za-z]+)\s+(\d{1,2})$/)
  if (match) {
    const month = match[1]
    const day = parseInt(match[2], 10)
    return `${month} ${day}${getOrdinalSuffix(day)}`
  }
  return formatted
}

export const finalRoundDates = (event, opts = {}) => {
  const start = I18n.l('date.formats.state_label_start', event.final_tour_starts_at, opts)
  const end = I18n.l('date.formats.state_label_end', event.final_tour_ends_at, opts)
  return `${start} – ${end}`
}

export const dates = (event, tour) => {
  const tourStatus = getTourStatus(event);

  if (tour) {
    if (tour === 'trial') {
      return trialRoundDates(event)
    } else if (tour === 'main') {
      return mainRoundDates(event)
    } else if (tour === 'final') {
      return finalRoundDates(event)
    }
  }

  if (tourStatus === TourStatus.COMING_PREPARATORY || tourStatus === TourStatus.PREPARATORY) {
    return preparatoryRoundDates(event)
  } else if (tourStatus === TourStatus.COMING_TRIAL || tourStatus === TourStatus.TRIAL) {
    return trialRoundDates(event)
  } else {
    return mainRoundDates(event)
  }
}

const isActive = (tour, event) => {
  return event.atPreparatoryTour && tour === 'preparatory' ||
         event.atTrialTour && tour === 'trial' ||
         event.atMainTour && tour === 'main'
}

const Label = ({ tour, event }) => {
  if (!_.includes(event.availableTours, tour)) { return null; }

  const classes = classnames(
    "state-label",
    { "state-label--active": isActive(tour, event) }
  )

  return (
    <div className={classes}>
      <div className="state-label__heading">
        { heading(tour) }:
      </div>
      <div className="state-label__date">
        { dates(event, tour) }
      </div>
    </div>
  )
}

Label.propTypes = {
  tour: PropTypes.oneOf(['preparatory', 'trial', 'main']).isRequired,
  event: PropTypes.instanceOf(Event).isRequired
}

export const Subject = ({ event, className, iconClassName, tourName }) => {
  return (
    <div className="h-flex-layout h-flex-layout--row-inline h-flex-layout--ai-center">
      <SVG src={marker} className={classnames(iconClassName)}/>
      <h2 className={classnames("olympiad-subject", className)}>
        {tourName || event.subjectName}
      </h2>
    </div>
  )
}

export const Dates = ({event, className, iconClassName, tour}) => {
  return (
    <div className="h-flex-layout h-flex-layout--row-inline h-flex-layout--ai-center">
      <SVG src={calendar} className={classnames(iconClassName)}/>
      <div className={classnames("ml-gutter-s", "f--md", "nowrap", className)}>
        {dates(event, tour)}
      </div>
    </div>
  )
}

export const Timing = ({event, className, iconClassName, textTiming}) => {
  let timing;

  if (textTiming) {
    timing = textTiming
  } else {
    timing = event.currentTour === "main" ?
      I18n.t('landing.main_round_time') :
      I18n.t('landing.trial_round_time')
  }

  return (
    <div className="h-flex-layout h-flex-layout--row-inline h-flex-layout--ai-center">
      <SVG src={clocks} className={classnames(iconClassName)} style={{ alignSelf: 'flex-start', marginTop: '4px' }}/>
      <div className={classnames("ml-gutter-s", "f--md", className)} style={{ maxWidth: '440px' }}>
        {timing}
      </div>
    </div>
  )
}

export default Label
